import {
    Card,
    CardContent,
    CardFooter,
    CardHeader,
} from "@/components/ui/card";
import { cn } from "@/lib/utils";
import Image from "next/image";
import posthog from "posthog-js";
import { useEffect, useState } from "react";

interface StepCardProps {
    title: string;
    description: string;
    subtext?: string;
    imageSrc: string;
    index: number;
    testGroup: boolean;
}

const StepCard = ({
    title,
    description,
    subtext,
    imageSrc,
    index,
    testGroup,
}: StepCardProps) => {
    if (testGroup) {
        return (
            <Card className="w-full p-6 pt-10 flex flex-col lg:flex-row relative overflow-hidden">
                <CardContent className="p-0 flex flex-col gap-3">
                    <div className="flex items-center gap-2 text-[#1a5b77] dark:text-blue-400 text-2xl font-medium font-satoshi leading-relaxed">
                        {index + 1}. {title}
                    </div>
                    <div className="flex flex-col grow justify-between">
                        <div className="text-[#333333] dark:text-gray-200 text-base font-light font-space leading-snug">
                            {description}
                        </div>
                        {subtext && (
                            <p className="text-[#333333]/80 dark:text-gray-400 text-base font-medium font-space leading-snug mt-auto pt-2">
                                {subtext}
                            </p>
                        )}
                    </div>
                </CardContent>
                <div className="w-full flex items-start justify-center lg:justify-end pl-4">
                    <Image
                        width={159}
                        height={159}
                        src={imageSrc}
                        alt={`Step ${index + 1}`}
                        className="object-contain"
                    />
                </div>
            </Card>
        );
    }
    return (
        <Card
            className={cn(
                "w-full flex flex-col",
                "bg-[#f8f9fa] dark:bg-gray-700", // Preserve original background color
            )}
        >
            <CardContent className="p-6 pt-10">
                {/* Image and Number Container */}
                <div
                    className="relative mb-8 flex items-end"
                    style={{ height: "160px" }}
                >
                    <Image
                        width={159}
                        height={159}
                        src={imageSrc}
                        alt={`Step ${index + 1}`}
                        className="mt-10"
                    />
                    <span className="absolute -top-4 right-0 text-[96px] font-bold text-[#1a5b77]/10 dark:text-blue-400/10 font-satoshi">
                        <Image
                            src={`/images/marketing/HowDoesItWorkNumber${index + 1}.png`}
                            alt={`Step ${index + 1}`}
                            width={96}
                            height={96}
                        />
                    </span>
                </div>

                {/* Content */}
                <div className="flex flex-col md:grow gap-3">
                    <div className="text-[#333333] dark:text-white text-2xl font-medium font-satoshi leading-relaxed">
                        {title}
                    </div>
                    <div className="flex flex-col md:grow md:justify-between">
                        <div className="text-[#333333] dark:text-gray-200 text-base font-light font-space leading-snug">
                            {description}
                        </div>
                        {subtext && (
                            <p className="text-[#333333]/80 dark:text-gray-400 text-base font-medium font-space leading-snug mt-auto pt-2">
                                {subtext}
                            </p>
                        )}
                    </div>
                </div>
            </CardContent>
        </Card>
    );
};

const HowDoesItWork = () => {
    const [testGroup, setTestGroup] = useState<string | boolean | undefined>(
        undefined,
    );
    const [isClient, setIsClient] = useState(false);

    useEffect(() => {
        setIsClient(true);
        // posthog.featureFlags.override({ "treatment-pages-steps": "test_group_2" });
        const flagValue = posthog.getFeatureFlag("treatment-pages-steps");
        setTestGroup(flagValue);
    }, [posthog]);

    // Use default steps during SSR and initial client render to prevent hydration mismatch
    let steps = [
        {
            title: "Fill out a condition assessment.",
            description:
                "Share your symptoms, current conditions, allergies and complete a health assessment.",
            imageSrc: "/images/marketing/HowDoesItWork1.png",
        },
        {
            title: "Our clinicians will review your health profile.",
            description:
                "An expert clinician from RTHM's provider team will review your case and prescribe if appropriate.",
            subtext: "Pay zero fees if you're not approved.",
            imageSrc: "/images/marketing/HowDoesItWork2.png",
        },
        {
            title: "Get approved and pick up your prescription.",
            description:
                "Pick up and pay for the prescribed treatments at your pharmacy. Start your treatment in as early as 1 business day.",
            imageSrc: "/images/marketing/HowDoesItWork3.png",
        },
    ];

    // Only apply feature flag variations after client has mounted to prevent hydration mismatch
    if (isClient && testGroup === "test_group_1") {
        steps = [
            {
                title: "Fill out a Safety Assessment",
                description:
                    "Your condition is unique - so we make sure this treatment is safe for you.  Our RTHM Complete Safety Assessment is thorough.  We look at your previous diagnoses, current medications, and symptoms to help us highlight any potential comorbidities, treatment interactions, etc. - in short, your safety is our first concern. ",
                imageSrc: "/images/marketing/HowDoesItWork1.png",
            },
            {
                title: "Our Complex Illness Clinicians review your health assessment",
                description:
                    "An expert clinician in complex illnesses like Long COVID, ME/CFS, MCAS, POTS, etc from RTHM will review your case and approve your prescription if safe.  They will also build your dosage plan, which might mean starting at a low dose to make sure you don't react poorly to the treatment. Pay zero fees if you're not approved",
                subtext: "Pay zero fees if you're not approved.",
                imageSrc: "/images/marketing/HowDoesItWork2.png",
            },
            {
                title: "Get approved and pick up your prescription.",
                description:
                    "Pick up and pay for the prescribed treatments at your pharmacy. You can also use a compounding pharmacy if you have ingredient sensitivities.  Then start your treatment as early as 1 business day.  And if you have an adverse reaction, you can always reach out to our Care Team for support",
                imageSrc: "/images/marketing/HowDoesItWork3.png",
            },
        ];
    } else if (isClient && testGroup === "test_group_2") {
        steps = [
            {
                title: "Fill out a Safety Assessment",
                description:
                    "Your condition is unique - so we make sure this treatment is safe for you. Our RTHM Complete Safety Assessment is thorough. We look at your previous diagnoses, current medications, and symptoms to help us highlight any potential comorbidities, treatment interactions, etc. - in short, your safety is our first concern.",
                imageSrc: "/images/marketing/HowDoesItWork1.png",
            },
            {
                title: "Our Complex Illness Clinicians review your health assessment",
                description:
                    "An expert clinician in complex illnesses like Long COVID, ME/CFS, MCAS, POTS, etc from RTHM will review your case and approve your prescription if safe. They will also build your dosage plan, which might mean starting at a low dose to make sure you don't react poorly to the treatment.",
                subtext: "Pay zero fees if you're not approved.",
                imageSrc: "/images/marketing/HowDoesItWork2.png",
            },
            {
                title: "Talk to a RTHM Clinician about your medication",
                description:
                    "Once a clinician reviews your assessment and approves the medication, you will have the option to have one Medication Consultation with a RTHM clinician. Use it to discuss questions about your prescriptions or to address any concerns or side effects from the medication. We've got you covered.",
                imageSrc: "/images/marketing/HowDoesItWork6.png",
            },
            {
                title: "Get approved and pick up your prescription",
                description:
                    "Pick up and pay for the prescribed treatments at your pharmacy. You can also use a compounding pharmacy if you have ingredient sensitivities. Then start your treatment as early as 1 business day. And if you have an adverse reaction, you can always reach out to our Care Team for support",
                imageSrc: "/images/marketing/HowDoesItWork3.png",
            },
        ];
    }

    let containerClass = "flex flex-col md:flex-row justify-center gap-6";
    if (isClient && testGroup === "test_group_2") {
        containerClass = "grid grid-cols-1 md:grid-cols-2 gap-6";
    }

    return (
        <div className="py-8">
            <div className="flex flex-col items-center gap-5 mb-11">
                <h2 className="text-[#333333] dark:text-white text-3xl md:text-5xl font-semibold font-satoshi uppercase leading-tight text-center">
                    How does it work?
                </h2>
                <p className="text-[#333333] dark:text-gray-300 text-xl font-normal font-space leading-7 text-center">
                    Access to medications as easy as{" "}
                    <span className="whitespace-nowrap">1-2-3</span>
                </p>
            </div>

            <div className={containerClass}>
                {steps.map((step, index) => (
                    <StepCard
                        key={index}
                        {...step}
                        index={index}
                        testGroup={isClient && testGroup === "test_group_2"}
                    />
                ))}
            </div>
        </div>
    );
};

export default HowDoesItWork;
